import _ from 'lodash';
import { SanityFieldType } from 'utility/node/sanity/products.model';
import { ITiaParty } from 'utility/node/tia-rest/model/response/party.response.model';
import { addMessageWithGroup, SentryGroup } from 'utility/sentry';
import * as yup from 'yup';

export type ValidationType =
  | 'regex'
  | 'min'
  | 'max'
  | 'required'
  | 'isTrue'
  | 'isFalse'
  | 'email'
  | 'dawaAddress'
  | 'validBankRegNo'
  | 'validBankAccountNo'
  | 'maxAgeFromCpr'
  | 'minAgeFromCpr';

export type ValueType = 'field' | 'static' | 'partyData' | 'dynamicField';

export interface Validation {
  type?: ValidationType;
  typeField?: 'greaterThanOrEqualField' | 'lessThanOrEqualField' | 'equalField';
  valueType?: ValueType;
  valueFieldId?: string;
  valueFieldType?: SanityFieldType;
  valueFieldRiskNo?: number;
  dateProps?: {
    ignoreTime?: boolean;
    useCurrentDate?: boolean;
  };
  value?: string | number | boolean;
  errorMessage: string;
  partyDataAttribute?: string;
}

export const stringValidations = (
  validations: Validation[],
  values?: any,
  party?: ITiaParty,
) => {
  let stringValidation = yup.string();

  if (validations) {
    validations.forEach((validation) => {
      let value;
      if (validation.valueType === 'partyData') {
        value = _.get(party, validation.partyDataAttribute);
        validation.value = value;
      } else {
        value = _.get(values, validation.valueFieldId);
      }
      const combinedTypes =
        validation.valueType === 'field'
          ? validation.typeField
          : validation.valueType === 'dynamicField'
            ? 'dynamicField'
            : validation.type;
      switch (combinedTypes) {
        case 'greaterThanOrEqualField':
          stringValidation = stringValidation.greaterThanOrEqualField(
            validation.errorMessage,
            value,
          );
          break;
        case 'lessThanOrEqualField':
          stringValidation = stringValidation.lessThanOrEqualField(
            validation.errorMessage,
            value,
          );
          break;
        case 'equalField':
          stringValidation = stringValidation.equalField(
            validation.errorMessage,
            value,
          );
          break;
        case 'dawaAddress':
          stringValidation = stringValidation.validDawaAddress(
            validation.errorMessage,
          );
          break;
        case 'maxAgeFromCpr':
          stringValidation = stringValidation.maxAgeFromCpr(
            validation.errorMessage,
            validation.value as number,
          );
          break;
        case 'minAgeFromCpr':
          stringValidation = stringValidation.minAgeFromCpr(
            validation.errorMessage,
            validation.value as number,
          );
          break;
        case 'validBankRegNo':
          stringValidation = stringValidation.validBankRegNo(
            validation.errorMessage,
          );
          break;
        case 'validBankAccountNo':
          stringValidation = stringValidation.validBankAccountNo(
            validation.errorMessage,
          );
          break;
        case 'required':
          stringValidation = stringValidation.required(validation.errorMessage);
          break;
        case 'regex':
          if (validation.value) {
            stringValidation = stringValidation.matches(
              new RegExp(validation.value.toString(), 'i'),
              validation.errorMessage,
            );
          } else {
            const message = `Value is needed when validation type is ${validation.type}`;
            addMessageWithGroup({
              message: message,
              group: SentryGroup.validation,
              tag: 'getTiaValidateBankAccountNo',
              printToConsole: true,
              consoleMessage: message,
            });
          }

          break;
        case 'email':
          stringValidation = stringValidation.email(validation.errorMessage);

          break;
        case 'min':
          const min = validation.value?.toString();
          const minNumberValue = parseInt(min || '0');

          if (validation.value && minNumberValue) {
            stringValidation = stringValidation.min(
              minNumberValue,
              validation.errorMessage,
            );
          } else {
            const message = `Value is needed and should be a number when validation type is ${validation.type}`;
            addMessageWithGroup({
              message: message,
              group: SentryGroup.validation,
              tag: 'getTiaValidateBankAccountNo',
              printToConsole: true,
              consoleMessage: message,
            });
          }
          break;
        case 'max':
          const max = validation.value?.toString();
          const maxNumberValue = parseInt(max || '999');

          if (validation.value && maxNumberValue) {
            stringValidation = stringValidation.max(
              maxNumberValue,
              validation.errorMessage,
            );
          } else {
            const message = `Value is needed and should be a number when validation type is ${validation.type}`;
            addMessageWithGroup({
              message: message,
              group: SentryGroup.validation,
              tag: 'getTiaValidateBankAccountNo',
              printToConsole: true,
              consoleMessage: message,
            });
          }
          break;
        case 'dynamicField':
          stringValidation = stringValidation.dynamicField(
            validation.errorMessage,
            value,
            validation.value,
          );
          break;
        default:
          const message = `Wizard validation: Validation of type ${validation.type} not implemented for question type boolean`;
          addMessageWithGroup({
            message: message,
            group: SentryGroup.validation,
            tag: 'getTiaValidateBankAccountNo',
            printToConsole: true,
            consoleMessage: message,
          });
          break;
      }
    });
  }
  return stringValidation;
};

export const checkboxValidations = (validations: Validation[]) => {
  let boolValidation = yup.bool();

  if (validations) {
    validations.forEach((validation) => {
      switch (validation.type) {
        case 'required':
        case 'isTrue':
          boolValidation = boolValidation.isTrue(validation.errorMessage);
          break;
        case 'isFalse':
          boolValidation = boolValidation.isFalse(validation.errorMessage);
        case 'regex':
        case 'min':
        case 'max':
        case 'email':
        default:
          const message = `Validation of type ${validation.type} not implemented for question type string`;
          addMessageWithGroup({
            message: message,
            group: SentryGroup.validation,
            tag: 'checkboxValidations',
            printToConsole: true,
            consoleMessage: message,
          });
      }
    });
  }

  return boolValidation;
};

export const dateValidations = (validations: Validation[], values?: any) => {
  let dateValidation = yup.date().nullable().typeError('Ugyldig dato');

  if (validations) {
    validations.forEach((validation) => {
      const maxDate = new Date();
      maxDate.setUTCHours(29, 59, 59, 999);

      const getTime = (validation: Validation): Date => {
        const currentTime = validation?.dateProps.useCurrentDate;
        const ignoreTime = validation?.dateProps.ignoreTime;

        if (!validation.value && !currentTime) {
          const message = `Wizard validation: Value is needed when validation type is ${validation.type}`;
          addMessageWithGroup({
            message: message,
            group: SentryGroup.validation,
            tag: 'checkboxValidations',
            printToConsole: true,
            consoleMessage: message,
          });
          return undefined;
        }

        if (typeof validation.value === 'boolean') {
          addMessageWithGroup({
            message: `Wizard validation: Boolean value is not supported when validation type is ${validation.type}`,
            group: SentryGroup.validation,
            tag: 'booleanValidations',
          });
          return undefined;
        }

        let time = currentTime ? new Date() : new Date(validation.value);

        if (ignoreTime && validation.type === 'max') {
          time.setUTCHours(24, 59, 59, 999);
        }
        if (ignoreTime && validation.type === 'min') {
          time.setUTCHours(0, 0, 0, 0);
        }

        return time;
      };

      const validationType =
        validation.valueType === 'dynamicField'
          ? 'dynamicField'
          : validation.type;

      switch (validationType) {
        case 'required':
          dateValidation = dateValidation.required(validation.errorMessage);
          break;
        case 'min':
          const minDate = getTime(validation);
          if (minDate) {
            dateValidation = dateValidation.min(
              minDate.toISOString(),
              validation.errorMessage,
            );
          }
          break;

        case 'max':
          const maxDate = getTime(validation);
          if (maxDate) {
            dateValidation = dateValidation.max(
              maxDate.toISOString(),
              validation.errorMessage,
            );
          }
        case 'dynamicField':
          const value = _.get(values, validation.valueFieldId);
          if (value) {
            dateValidation = dateValidation.dynamicField(
              validation.errorMessage,
              validation.value,
              value,
            );
          }
          break;
        case 'regex':
        case 'email':
        default:
          const message = `Wizard validation: Validation of type ${validation.type} not implemented for question type string`;
          addMessageWithGroup({
            message: message,
            group: SentryGroup.validation,
            tag: 'unknownValidations',
            printToConsole: true,
            consoleMessage: message,
          });
      }
    });
  }

  return dateValidation;
};

export const numberValidations = (
  validations: Validation[],
  values?: any,
  party?: ITiaParty,
) => {
  let numberValidation = yup.number();
  if (validations) {
    validations.forEach((validation) => {
      let value;
      if (validation.valueType === 'partyData') {
        value = _.get(party, validation.partyDataAttribute);
        validation.value = value;
      } else {
        value = _.get(values, validation.valueFieldId);
      }

      const combinedTypes =
        validation.valueType === 'field'
          ? validation.typeField
          : validation.type;
      switch (combinedTypes) {
        case 'greaterThanOrEqualField':
          numberValidation = numberValidation.greaterThanOrEqualField(
            validation.errorMessage,
            value,
          );
          break;
        case 'lessThanOrEqualField':
          numberValidation = numberValidation.lessThanOrEqualField(
            validation.errorMessage,
            value,
          );
          break;
        case 'equalField':
          numberValidation = numberValidation.equalField(
            validation.errorMessage,
            value,
          );
        case 'required':
          numberValidation = numberValidation.required(validation.errorMessage);
          break;
        case 'min':
          const min = validation.value?.toString();
          const minNumberValue = parseInt(min || '0');

          if (validation.value && minNumberValue) {
            numberValidation = numberValidation.min(
              minNumberValue,
              validation.errorMessage,
            );
          } else {
            const message = `Wizard validation: Value is needed and should be a number when validation type is ${validation.type}`;
            addMessageWithGroup({
              message: message,
              group: SentryGroup.validation,
              tag: 'numberValidations',
              printToConsole: true,
              consoleMessage: message,
            });
          }
          break;
        case 'max':
          const max = validation.value?.toString();
          const maxNumberValue = parseInt(max || '999');

          if (validation.value && maxNumberValue) {
            numberValidation = numberValidation.max(
              maxNumberValue,
              validation.errorMessage,
            );
          } else {
            const message = `Wizard validation: Value is needed and should be a number when validation type is ${validation.type}`;
            addMessageWithGroup({
              message: message,
              group: SentryGroup.validation,
              tag: 'numberValidations',
              printToConsole: true,
              consoleMessage: message,
            });
          }
          break;
        case 'email':
        case 'regex':
        default:
          const message = `Wizard validation: Validation of type ${validation.type} not implemented for question type number`;
          addMessageWithGroup({
            message: message,
            group: SentryGroup.validation,
            tag: 'numberValidations',
            printToConsole: true,
            consoleMessage: message,
          });
      }
    });
  }

  return numberValidation;
};

export const consentValidations = (validations: Validation[]) => {
  let objectValidation = yup.object().nullable();

  validations.forEach((validation) => {
    switch (validation.type) {
      case 'required':
        objectValidation = objectValidation.required(validation.errorMessage);
        break;
      default:
        const message = `Wizard validation: Validation of type ${validation.type} not implemented for question type consent`;
        addMessageWithGroup({
          message: message,
          group: SentryGroup.validation,
          tag: 'consentValidations',
          printToConsole: true,
          consoleMessage: message,
        });
    }
  });
  return objectValidation;
};
